import {
    type OpenApiSchemaAggregatedProductCardListItem,
    type OpenApiSchemaLightAggregatedProductCardListItem,
    type OpenApiSchemaLightProductVariant,
    type OpenApiSchemaProductVariantListItem,
} from '#shared/api/schema';
import { createPaginateDataGridProvider, type GridRowExtended } from '#shared/components/grid';
import { api, cardGridTagInvalidator } from '../api/api';

export type ProductCardGridSubRow =
    | GridRowExtended<OpenApiSchemaProductVariantListItem>
    | GridRowExtended<OpenApiSchemaLightProductVariant>;
type ProductCardGridItem = OpenApiSchemaAggregatedProductCardListItem | OpenApiSchemaLightAggregatedProductCardListItem;
export type ProductCardGridRow = GridRowExtended<ProductCardGridItem, ProductCardGridSubRow>;

export type ProductCardGridRowOrSubrow = ProductCardGridRow | ProductCardGridSubRow;

export const useProductCardsDataGridProvider = createPaginateDataGridProvider({
    getPageNumber: (params) => params.payload.page ?? 0,
    getPerPage: (params) => params.payload.per_page ?? 30,
    getTotal: (result) => result.total_count,
    getRows: (result): ProductCardGridRow[] =>
        (result.cards || []).map((item) => {
            return {
                id: item.id,
                attributes: item,
                isParentRow: true,
                isSubRow: false,
                subRows: item.variants.map((variant) => {
                    return {
                        id: item.id + '_' + variant.id,
                        attributes: variant,
                        isSubRow: true,
                        isParentRow: false,
                    };
                }),
            };
        }),
    getItemFromRow: (row: ProductCardGridRowOrSubrow) => row.attributes,
    getSelectableId: (_, rows) => {
        const ids: string[] = [];

        rows.forEach((row) => {
            ids.push(row.id);
            row.subRows?.forEach((subRow) => {
                ids.push(subRow.id);
            });
        });

        return ids;
    },
    endpoint: api.endpoints.getCardsForGrid,
    invalidateTagHelper: cardGridTagInvalidator,
});

export const mapLightProductCardsToGridRows = (
    cards: OpenApiSchemaLightAggregatedProductCardListItem[]
): ProductCardGridRow[] =>
    cards.map((item) => {
        return {
            id: item.id,
            attributes: item,
            isParentRow: true,
            isSubRow: false,
            subRows: item.variants.map((variant) => {
                return {
                    id: item.id + '_' + variant.id,
                    attributes: variant,
                    isSubRow: true,
                    isParentRow: false,
                };
            }),
        };
    });

export const useLightProductCardsDataGridProvider = createPaginateDataGridProvider({
    getPageNumber: (params) => params.payload.page ?? 0,
    getPerPage: (params) => params.payload.per_page ?? 30,
    getTotal: (result) => result.total_count,
    getRows: (result): ProductCardGridRow[] => mapLightProductCardsToGridRows(result.cards || []),
    getItemFromRow: (row: ProductCardGridRowOrSubrow) => row.attributes,
    getSelectableId: (_, rows) => rows.map((row) => row.id),
    endpoint: api.endpoints.getLightProductCards,
});
